import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/common/Common';
import { apiFetch } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { Search, RotateCcw, Download, X, ChevronDown, ExternalLink, TrendingUp, TrendingDown, FileText, Loader2 } from 'lucide-react';
import { generateUserReportPDF } from './reportPDF';
import UserReportView from './UserReportView';
import './LeadStatusReport.css';

const formatDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const todayStr = formatDate(new Date());
const monthStartStr = formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

const LeadsReport = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Filters (form state)
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selected, setSelected] = useState([]);
  const [startDate, setStartDate] = useState(monthStartStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Report data (only populated after Submit)
  const [submitted, setSubmitted] = useState(false);
  const [summary, setSummary] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  // Full per-user report (mobile-style sections) for the active detail tab
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  // Initial: fetch users for dropdown only (no report)
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('reports/lead-status?users_only=1');
        if (res.success) {
          setUsers(res.data?.users || []);
          setStatuses(res.data?.statuses || []);
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const runReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selected.length) params.set('user_ids', selected.join(','));
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      const res = await apiFetch(`reports/lead-status?${params.toString()}`);
      if (res.success) {
        setStatuses(res.data?.statuses || []);
        setSummary(res.data?.summary || []);
        setActiveTab(res.data?.summary?.[0]?.u_id || null);
        setSubmitted(true);
      } else {
        showToast(res.message || 'Failed to load report', 'error');
      }
    } catch {
      showToast('Connection failed', 'error');
    }
    setLoading(false);
  };

  const handleReset = () => {
    setSelected([]);
    setStartDate(monthStartStr);
    setEndDate(todayStr);
    setSearch('');
    setSubmitted(false);
    setSummary([]);
    setActiveTab(null);
  };

  // Fetch the full per-user report and generate the mobile-format PDF
  const downloadUserPdf = async (uid) => {
    setPdfLoadingId(uid);
    try {
      const res = await apiFetch(`reports/users/${uid}?start_date=${startDate}&end_date=${endDate}`);
      if (res.success) {
        generateUserReportPDF(res.data);
        logExport('pdf', 1, { user_id: uid });
      }
      else showToast(res.message || 'Failed to load report', 'error');
    } catch {
      showToast('Failed to generate report', 'error');
    }
    setPdfLoadingId(null);
  };

  // Load the full per-user report for the active tab (Performance, status
  // breakdown, calls, attendance, etc.) — mirrors the mobile app layout.
  useEffect(() => {
    if (!activeTab) { setUserDetail(null); return; }
    let cancelled = false;
    (async () => {
      setUserDetailLoading(true);
      try {
        const res = await apiFetch(`reports/users/${activeTab}?start_date=${startDate}&end_date=${endDate}`);
        if (!cancelled) setUserDetail(res.success ? res.data : null);
      } catch {
        if (!cancelled) setUserDetail(null);
      }
      if (!cancelled) setUserDetailLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, startDate, endDate]);

  const togglePick = (uid) => setSelected(p => p.includes(uid) ? p.filter(x => x !== uid) : [...p, uid]);
  const toggleAll = () => setSelected(selected.length === users.length ? [] : users.map(u => u.u_id));
  const removePick = (uid) => setSelected(p => p.filter(x => x !== uid));

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.role_name || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const totalsRow = useMemo(() => {
    const by_status = {};
    let total = 0, total_leads = 0, total_activities = 0, total_calls = 0, call_duration_mins = 0, present_days = 0;
    summary.forEach(r => {
      total += r.total || 0;
      total_leads += r.total_leads || 0;
      total_activities += r.total_activities || 0;
      total_calls += r.total_calls || 0;
      call_duration_mins += r.call_duration_mins || 0;
      present_days += r.present_days || 0;
      Object.entries(r.by_status || {}).forEach(([sid, cnt]) => {
        by_status[sid] = (by_status[sid] || 0) + cnt;
      });
    });
    return { total, by_status, total_leads, total_activities, total_calls, call_duration_mins, present_days };
  }, [summary]);

  const bookingStatusId = useMemo(() => {
    const s = statuses.find(x => /booking/i.test(x.name) && /done|complete/i.test(x.name));
    return s ? s.ls_id : null;
  }, [statuses]);
  const conv = (row) => {
    if (!bookingStatusId || !row.total) return 0;
    const booked = row.by_status?.[bookingStatusId] || 0;
    return Math.round((booked / row.total) * 100);
  };

  // Fire-and-forget audit log for a client-side export (CSV/PDF). The server
  // never sees the export itself, so we report it explicitly for leak tracking.
  const logExport = (format, count, extra = {}) => {
    apiFetch('audit/log', {
      method: 'POST',
      body: JSON.stringify({
        action: 'export',
        entity_type: 'lead',
        details: { format, count, report: 'leads-report', ...extra },
      }),
    }).catch(() => {});
  };

  const exportCSV = (rows, filename, isDetail = false) => {
    if (!rows.length) return;
    logExport('csv', rows.length, { detail: isDetail });
    const headers = isDetail
      ? ['Date', 'User', 'Total', ...statuses.map(s => s.name)]
      : ['User', 'Role', 'Assigned Leads', 'Activities', 'Calls', 'Call Mins', 'Present Days', 'Trend %', 'Total Touched', ...statuses.map(s => s.name), 'Conversion %'];
    const escape = (v) => {
      const s = String(v == null ? '' : v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(',')];
    rows.forEach(row => {
      if (isDetail) {
        const u = summary.find(x => x.u_id === row.u_id);
        const cells = [row.date, u?.full_name || u?.username || '', row.total || 0, ...statuses.map(s => row.by_status?.[s.ls_id] || 0)];
        lines.push(cells.map(escape).join(','));
      } else {
        const cells = [
          row.full_name || row.username, row.role_name || '',
          row.total_leads || 0, row.total_activities || 0, row.total_calls || 0,
          row.call_duration_mins || 0, row.present_days || 0,
          row.change_percent == null ? '' : `${row.change_percent}%`,
          row.total || 0, ...statuses.map(s => row.by_status?.[s.ls_id] || 0), `${conv(row)}%`,
        ];
        lines.push(cells.map(escape).join(','));
      }
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const renderCount = (n) => (
    <span className={`lsr-cell-count ${!n ? 'lsr-cell-count--zero' : ''}`}>{n || 0}</span>
  );
  const renderTrend = (p) => {
    if (p === null || p === undefined) return <span style={{ color: 'var(--gray-300)' }}>—</span>;
    if (p >= 0) return <span style={{ color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: 3 }}><TrendingUp size={12} />{p}%</span>;
    return <span style={{ color: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: 3 }}><TrendingDown size={12} />{p}%</span>;
  };

  return (
    <div>
      <Header title="Leads Report" subtitle="Filter users and date range, then run the report" />
      <div className="page">
        {/* Filters */}
        <div className="lsr-card">
          <h3 className="lsr-card__title">Generate Lead Reports</h3>
          <div className="lsr-filters">
            <div className="lsr-field" ref={menuRef}>
              <label className="lsr-field__label">Select Users</label>
              <div className="lsr-multiselect">
                <div className="lsr-multiselect__pillbox" onClick={() => setMenuOpen(true)}>
                  {selected.map(uid => {
                    const u = users.find(x => x.u_id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="lsr-pill">
                        {u.full_name || u.username}
                        <span className="lsr-pill__x" onClick={(e) => { e.stopPropagation(); removePick(uid); }}><X size={12} /></span>
                      </span>
                    );
                  })}
                  <input className="lsr-multiselect__input"
                    placeholder={selected.length ? '' : 'All users (default)'}
                    value={search}
                    onFocus={() => setMenuOpen(true)}
                    onChange={(e) => setSearch(e.target.value)} />
                  <ChevronDown size={14} style={{ alignSelf: 'center', color: 'var(--gray-400)' }} />
                </div>
                {menuOpen && (
                  <div className="lsr-multiselect__menu">
                    <div className="lsr-multiselect__option lsr-multiselect__option--all" onClick={toggleAll}>
                      <input type="checkbox" readOnly checked={users.length > 0 && selected.length === users.length} />
                      <span>Select All</span>
                    </div>
                    {filteredOptions.map(u => (
                      <div key={u.u_id} className="lsr-multiselect__option" onClick={() => togglePick(u.u_id)}>
                        <input type="checkbox" readOnly checked={selected.includes(u.u_id)} />
                        <span>{u.full_name || u.username} <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>· {u.role_name}</span></span>
                      </div>
                    ))}
                    {filteredOptions.length === 0 && <div className="lsr-multiselect__option" style={{ color: 'var(--gray-400)' }}>No users match</div>}
                  </div>
                )}
              </div>
            </div>
            <div className="lsr-field">
              <label className="lsr-field__label">Date From</label>
              <input className="lsr-field__input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="lsr-field">
              <label className="lsr-field__label">Date To</label>
              <input className="lsr-field__input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="lsr-actions">
            <Button variant="outline" icon={RotateCcw} onClick={handleReset}>Reset</Button>
            <Button variant="gold" icon={Search} onClick={runReport} disabled={loading}>{loading ? 'Loading…' : 'Search Reports'}</Button>
          </div>
        </div>

        {!submitted ? (
          <div className="lsr-card">
            <div className="lsr-empty">Pick filters above and click <strong>Search Reports</strong> to see results.</div>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="lsr-section-title">
              <h3>Summary</h3>
              {summary.length > 0 && (
                <button className="lsr-export-btn" onClick={() => exportCSV(summary, 'leads-report-summary.csv')}>
                  <Download size={13} /> Export
                </button>
              )}
            </div>
            <div className="lsr-table-wrap">
              {summary.length === 0 ? (
                <div className="lsr-empty">No data for selected filters</div>
              ) : (
                <table className="lsr-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>Role</th>
                      <th className="lsr-num">Assigned</th>
                      <th className="lsr-num">Activities</th>
                      <th className="lsr-num">Calls</th>
                      <th className="lsr-num">Call Mins</th>
                      <th className="lsr-num">Present Days</th>
                      <th className="lsr-num">Trend</th>
                      <th className="lsr-num">Touched</th>
                      {statuses.map(s => <th key={s.ls_id} className="lsr-num" style={{ color: s.color }}>{s.name}</th>)}
                      <th className="lsr-num">Conv %</th>
                      <th className="lsr-num"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((row, i) => (
                      <tr key={row.u_id}>
                        <td>{i + 1}</td>
                        <td>
                          <button onClick={() => setActiveTab(row.u_id)}
                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--navy-700)', fontWeight: 600, cursor: 'pointer' }}>
                            {row.full_name || row.username}
                          </button>
                        </td>
                        <td><span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{row.role_name || '—'}</span></td>
                        <td className="lsr-num"><strong>{row.total_leads || 0}</strong></td>
                        <td className="lsr-num">{row.total_activities || 0}</td>
                        <td className="lsr-num">{row.total_calls || 0}</td>
                        <td className="lsr-num">{row.call_duration_mins || 0}</td>
                        <td className="lsr-num">{row.present_days || 0}</td>
                        <td className="lsr-num">{renderTrend(row.change_percent)}</td>
                        <td className="lsr-num"><strong>{row.total || 0}</strong></td>
                        {statuses.map(s => (
                          <td key={s.ls_id} className="lsr-num">{renderCount(row.by_status?.[s.ls_id])}</td>
                        ))}
                        <td className="lsr-num"><span className="lsr-conv">{conv(row)}%</span></td>
                        <td className="lsr-num">
                          <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                            <button title="Download PDF report" disabled={pdfLoadingId === row.u_id}
                              onClick={() => downloadUserPdf(row.u_id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
                              {pdfLoadingId === row.u_id
                                ? <Loader2 size={14} className="spin" />
                                : <FileText size={14} />}
                            </button>
                            <button title="View profile" onClick={() => navigate(`/leads-report/${row.u_id}`)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
                              <ExternalLink size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {summary.length > 1 && (
                      <tr className="lsr-row--total">
                        <td colSpan={3}>Total</td>
                        <td className="lsr-num">{totalsRow.total_leads}</td>
                        <td className="lsr-num">{totalsRow.total_activities}</td>
                        <td className="lsr-num">{totalsRow.total_calls}</td>
                        <td className="lsr-num">{totalsRow.call_duration_mins}</td>
                        <td className="lsr-num">{totalsRow.present_days}</td>
                        <td className="lsr-num">—</td>
                        <td className="lsr-num">{totalsRow.total}</td>
                        {statuses.map(s => <td key={s.ls_id} className="lsr-num">{totalsRow.by_status[s.ls_id] || 0}</td>)}
                        <td className="lsr-num">—</td>
                        <td className="lsr-num"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Detail */}
            {summary.length > 0 && (
              <>
                <div className="lsr-section-title">
                  <h3>Lead Report In Detail</h3>
                  {activeTab && userDetail && (
                    <button className="lsr-export-btn" disabled={pdfLoadingId === activeTab}
                      onClick={() => { try { generateUserReportPDF(userDetail); logExport('pdf', 1, { user_id: activeTab }); } catch { showToast('Failed to generate report', 'error'); } }}>
                      <Download size={13} /> Download PDF
                    </button>
                  )}
                </div>
                <div className="lsr-tabs">
                  {summary.map(row => (
                    <button key={row.u_id} className={`lsr-tab ${activeTab === row.u_id ? 'lsr-tab--active' : ''}`}
                      onClick={() => setActiveTab(row.u_id)}>
                      {row.full_name || row.username}
                    </button>
                  ))}
                </div>
                <div className="mrpt">
                  {userDetailLoading && !userDetail ? (
                    <div className="mrpt-loading"><Loader2 size={18} className="spin" /> Loading report…</div>
                  ) : userDetail ? (
                    <UserReportView detail={userDetail} />
                  ) : (
                    <div className="lsr-empty">No report data for this user</div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeadsReport;
